import IconButton from "@material-ui/core/IconButton";
import useDashboardEditor from "MyDashboards/state/editor";
import { EditorMode, Element } from "MyDashboards/state/editorTypes";
import { DashboardOpenMode } from "MyDashboards/state/types";
import { Dashboard } from "MyDashboards/types/api";
import px from "MyDashboards/utils/px";
import React, { FunctionComponent, useCallback } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
  ResponderProvided,
} from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { Else, If, Then } from "react-if";
import { ReactComponent as AddIcon } from "../../assets/addIcon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/deleteIcon.svg";
import { ReactComponent as ImportIcon } from "../../assets/importIcon.svg";
import AddSlideModal from "../AddSlideModal/AddSlideModal";
import CustomIconButton from "../CustomIconButton";
import ImportDashboardModal from "../ImportDashboardModal/ImportDashboardModal";
import * as renderers from "../Render";
import RenderSelectedSlide from "../SlideView/RenderSlide";
import useSlidesMenuStyles from "./themes";
import useSlidesMenuWidth from "./useSlidesMenuWidth";

interface SlidesMenuProps {
  openMode: DashboardOpenMode;
}

const RenderElement = (props: { el: Element }) => {
  const { el } = props;
  if (props.el.component) {
    if (props.el.component.component === "Highcharts") {
      return <div>Highcharts component {props.el.component.data.message}</div>;
    }
  }
  return (
    <div
      style={{
        width: px(el.width),
        height: px(el.height),
        background: "rgba(0,100,255,0.1)",
      }}
    />
  );
};
const SlidesMenu: FunctionComponent<SlidesMenuProps> = (props) => {
  const classes = useSlidesMenuStyles();
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = React.useState(false);
  const pages = useDashboardEditor((e) => e.pages);
  const addPage = useDashboardEditor((e) => e.addPage);
  const onDashboardSelected = (dashboard: Dashboard) => {
    setIsImportModalOpen(false);
    dashboard.dashboardData?.pages.forEach((p) => addPage(p));
  };
  const selectedPageId = useDashboardEditor((e) => e.selectedPageId);
  const setSelectedPage = useDashboardEditor((e) => e.setSelectedPage);
  const removePage = useDashboardEditor((e) => e.removeActivePage);
  const reorderPage = useDashboardEditor((e) => e.reorderPage);
  const setActiveElement = useDashboardEditor((s) => s.setSelectedElement);
  const editorMode = useDashboardEditor((e) => e.mode);
  const onBeforeCapture = useCallback(() => {
    /* ... */
  }, []);
  const onBeforeDragStart = useCallback(() => {
    /* ... */
  }, []);
  const onDragStart = useCallback(() => {
    /* ... */
  }, []);
  const onDragUpdate = useCallback(() => {
    /* ... */
  }, []);
  const onDragEnd = useCallback(
    (result: DropResult, provided: ResponderProvided) => {
      if (!result.destination) {
        return;
      }
      const srcIdx = result.source.index;
      const destIdx = result.destination.index;
      reorderPage(pages[srcIdx].id, destIdx);
    },
    [pages]
  );

  const width = useSlidesMenuWidth();

  return (
    <div className={classes.container} style={{ width: px(width) }}>
      <If condition={editorMode === EditorMode.edit}>
        <Then>
          <div className={classes.toolbar}>
            <CustomIconButton
              tooltip={t("myStories.Add")}
              icon={<AddIcon style={{ color: "rgb(0,100,216)" }} />}
              label={width > 210 ? t("myStories.Add") : ""}
              onClick={() => {
                setIsAddModalOpen(true);
                setActiveElement(undefined);
              }}
            />
            <CustomIconButton
              tooltip={t("myStories.Import")}
              icon={<ImportIcon style={{ color: "rgb(0,100,216)" }} />}
              label={width > 210 ? t("myStories.Import") : ""}
              onClick={() => {
                setIsImportModalOpen(true);
                setActiveElement(undefined);
              }}
            />
            <IconButton
              title={t("myStories.Remove slide")}
              onClick={() => {
                removePage();
                setActiveElement(undefined);
              }}
            >
              <DeleteIcon style={{ fill: "currentColor", color: "#4C5157" }} />
            </IconButton>
          </div>
        </Then>
        <Else>
          <div className={classes.toolbar} style={{ border: "none" }}>
            <></>
          </div>
        </Else>
      </If>

      <DragDropContext
        onBeforeCapture={onBeforeCapture}
        onBeforeDragStart={onBeforeDragStart}
        onDragStart={onDragStart}
        onDragUpdate={onDragUpdate}
        onDragEnd={onDragEnd}
      >
        <Droppable
          droppableId="droppable-1"
          isDropDisabled={editorMode !== EditorMode.edit}
        >
          {(provided: any, snapshot: any) => (
            <div
              className={classes.list}
              style={{ width: px(width) }}
              ref={provided.innerRef}
            >
              {pages.map((page, idx) => {
                return (
                  <Draggable
                    draggableId={page.id}
                    index={idx}
                    key={page.id}
                    isDragDisabled={editorMode !== EditorMode.edit}
                  >
                    {(provided: any, snapshot: any) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        key={page.id}
                        style={{
                          marginLeft: px(width * 0.1),
                          marginRight: px(width * 0.1),
                          width: px(width * 0.8),
                          height: px(120 * (width / 240)),
                          ...provided.draggableProps.style,
                        }}
                      >
                        <div className={classes.slide}>
                          <span
                            className="number"
                            style={{ width: `${width * 0.0709}px` }}
                          >
                            {idx + 1}
                          </span>
                          <RenderSelectedSlide
                            RenderElement={RenderElement}
                            slide={page}
                            key={page.id}
                            width={width * 0.7}
                            containerClass={`slidePrev ${
                              page.id === selectedPageId ? "activeSlide" : ""
                            }`}
                            onClick={() => {
                              setSelectedPage(page.id);
                              setActiveElement(undefined);
                            }}
                            registry={renderers.thumbNails}
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <ImportDashboardModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onDashboardSelected={onDashboardSelected}
      />
      <AddSlideModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onLayoutSelected={(layout) => {
          addPage(layout);
          /**
           * Create new slide
           */
          setIsAddModalOpen(false);
        }}
      />
    </div>
  );
};

export default SlidesMenu;
